import { ReactNode, createContext, useEffect, useMemo } from 'react';
import { differenceInDays } from 'date-fns';
import { useDismissibleComponent } from 'commons/hooks';
import { PAYMENT_HARDBLOCK, PAYMENT_SOFTBLOCK } from 'config/upsellFlowSources';
import { useRoles } from 'domains/Auth/authSlice/useRoles';
import {
  useAdminDisabled,
  useAdminDisabledReason,
  useGetIsFreePlan,
  useGetStoreRemainingDaysToExpiration,
  useHasFirstPayment,
  useGetPlanDebt,
} from 'domains/Auth/hooks';
import {
  trackingBillingHardBlockModalShow,
  trackingBillingSoftBlockModalShow,
} from 'domains/Billing/tracking';

const noop = () => null;

export interface UpsellBlockContext {
  upsellSoftBlock: boolean;
  upsellHardBlock: boolean;
  dismiss: () => void;
}

export interface InterfaceUpsellBlockContextProvider {
  children: ReactNode;
}

export const UpsellBlockContext = createContext<UpsellBlockContext>({
  upsellSoftBlock: false,
  upsellHardBlock: false,
  dismiss: noop,
});

function UpsellBlockContextProvider({
  children,
}: Readonly<InterfaceUpsellBlockContextProvider>): JSX.Element {
  const isAdminDisabled = useAdminDisabled();
  const adminDisabledReason = useAdminDisabledReason();
  const hasFirstPayment = useHasFirstPayment();
  const remainingDaysToExpiration = useGetStoreRemainingDaysToExpiration();
  const { validateRoles } = useRoles();
  const { isFreePlan, isTrial } = useGetIsFreePlan();
  const { shouldShow, dismiss } = useDismissibleComponent(
    'upsell-soft-block',
    1,
  );
  const { planDebtData, shouldUseDebtData } = useGetPlanDebt();

  const isExpired =
    remainingDaysToExpiration !== undefined && remainingDaysToExpiration <= 0;
  const isAdminEnabled = !isAdminDisabled;
  const isFullRole = validateRoles('full');

  let upsellSoftBlock =
    isTrial &&
    !isFreePlan &&
    isFullRole &&
    isExpired &&
    isAdminEnabled &&
    shouldShow;

  let upsellHardBlock =
    isAdminDisabled && adminDisabledReason === 'expired' && !hasFirstPayment;

  if (shouldUseDebtData && planDebtData?.nextPaymentDate) {
    const today = new Date();
    const daysUntilNextPayment = differenceInDays(
      new Date(planDebtData.nextPaymentDate),
      today,
    );
    upsellSoftBlock =
      isTrial &&
      !isFreePlan &&
      isFullRole &&
      daysUntilNextPayment === 0 &&
      shouldShow;

    upsellHardBlock = isTrial && daysUntilNextPayment < 0;
  }

  const values = useMemo(
    () => ({
      upsellSoftBlock,
      upsellHardBlock,
      dismiss,
    }),
    [dismiss, upsellSoftBlock, upsellHardBlock],
  );

  useEffect(() => {
    if (upsellSoftBlock) {
      trackingBillingSoftBlockModalShow({
        featureKey: PAYMENT_SOFTBLOCK,
      });
    }

    if (upsellHardBlock) {
      trackingBillingHardBlockModalShow({
        featureKey: PAYMENT_HARDBLOCK,
      });
    }
  }, [upsellSoftBlock, upsellHardBlock]);

  return (
    <UpsellBlockContext.Provider value={values}>
      {children}
    </UpsellBlockContext.Provider>
  );
}

export default UpsellBlockContextProvider;
